import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import TextareaWithLabel from '../textarea'
import useMedRequestAction from '@/lib/api/med-requests/action'
import { isOpenedTreatmentPending } from '@/lib/med-requests'
import { cn, medReqFieldValidationNotifications } from '@/lib/utils'
import LoadingSpinner from '@/components/ui/icons/loading-spinner'
import SingleCombobox from '@/components/common/single-combobox'
import { useState } from 'react'
import useApproveMedicationRequest from '@/lib/api/med-requests/approve'
import { useQuestionnaire } from '@/hooks/useQuestionnaire'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { useInvestigationStore } from '@/lib/stores/investigation'
import { useCurrentInvestigation } from '@/hooks/useCurrentInvestigation'
import useApproveInvestigation from '@/lib/api/investigations/approve'
import useInvestigationAction from '@/lib/api/investigations/action'
import { useGetMessagingTemplate } from '@/hooks/useGetMessagingTemplate'
import InvestigationTemplatePreview from '../../investigation/template-preview'
import { getInvestigationLink } from '@/utils/investigations'
import useBatchApproval from '@/hooks/useBatchApproval'
import { X } from 'lucide-react'
import { canSendMessages } from '@/lib/permissions'
import { usePatientFilesGate } from '@/services/patientFiles'
import { mpsApprovalWaitReasonOf } from '@/lib/api/med-requests/mps-family-actions'

const hivResultText =
  'PRELIMINARY RESULT: REQUIRES CONFIRMATION. Further testing is needed. Please arrange for a confirmatory blood test from a vein (taken by a healthcare professional at your GP or sexual health clinic) to determine a definitive result.'

const ApprovalReasonSection = ({
  patientOrigin = 'medication-request'
}: {
  patientOrigin?: 'medication-request' | 'investigation'
}) => {
  const {
    justification: medReqJustification,
    setJustification: setMedReqJustification,
    setSidebarView: setMedReqSidebarView,
    medication,
    extraTreatments,
    removedTreatments,
    droppedProductHashes
  } = useMedicationRequestStore()
  const {
    justification: investigationJustification,
    setJustification: setInvestigationJustification,
    setSidebarView: setInvestigationSidebarView
  } = useInvestigationStore()

  const isMedReq = patientOrigin === 'medication-request'
  const justification = isMedReq ? medReqJustification : investigationJustification
  const setSidebarView = isMedReq ? setMedReqSidebarView : setInvestigationSidebarView
  const setJustification = isMedReq ? setMedReqJustification : setInvestigationJustification
  const fieldValidationNotifications = isMedReq ? medReqFieldValidationNotifications : () => true

  const medReqAction = useMedRequestAction({
    enabled: !!(patientOrigin === 'medication-request')
  })
  const investigationAction = useInvestigationAction()
  const action = isMedReq ? medReqAction : investigationAction

  const [template, setTemplate] = useState('')
  // Covers the whole submit — for MPS approves that spans the subscription sync plus one
  // approve per family member; `action.isPending` is this component's own instance and never
  // sees those.
  const [isSubmitting, setIsSubmitting] = useState(false)

  const investigation = useCurrentInvestigation(patientOrigin === 'investigation')
  const medRequest = useCurrentMedication(
    !!(patientOrigin === 'medication-request' || investigation?.data?.medicationRequestId),
    investigation?.data?.medicationRequestId,
    isMedReq
  )

  const { templateOptions } = useQuestionnaire(medRequest?.data?.questionaire ?? '')

  const {
    data: shareResultsTemplate,
    isLoading,
    error
  } = useGetMessagingTemplate({
    vendorHash: investigation.data?.vendorHash ?? '',
    templateName: 'conversation-share-results',
    data: {
      patient: {
        firstName: investigation.data?.patient.firstName ?? '',
        lastName: investigation.data?.patient.lastName ?? ''
      },
      testName: investigation.data?.results?.tests[0]?.testName ?? '',
      results: {
        tests:
          investigation.data?.results?.tests?.map(test => ({
            testName: test.testName,
            resultLines: test.resultLines?.map(line => ({
              testName: line.testName,
              result: line.result.toLocaleLowerCase().includes('reactive') ? hivResultText : line.result
            }))
          })) ?? []
      },
      learnMoreLink: investigation.data && getInvestigationLink(investigation.data).share
    },
    enabled: Boolean(!isMedReq && investigation?.data)
  })

  const approveMedicationRequest = useApproveMedicationRequest({
    patientOrigin
  })
  const approveInvestigation = useApproveInvestigation({
    patientOrigin,
    message: shareResultsTemplate ?? ''
  })
  const approveAction = isMedReq ? approveMedicationRequest : approveInvestigation
  const canSendMessagesRole = canSendMessages()

  // The footer's Approve is already gated, but a new file can land (focus
  // refetch) while this sidebar is open — re-check at submit, fail-closed.
  const filesGate = usePatientFilesGate(isMedReq ? medRequest?.data?.patient?.id : undefined)
  // Re-checked at submit too: the sidebar stays open while a sync or refetch moves the family.
  const approvalWaitReason = isMedReq
    ? mpsApprovalWaitReasonOf(medRequest?.data, { extraTreatments, removedTreatments, droppedProductHashes })
    : null

  const isDisabled =
    (isMedReq && !justification?.trim()) ||
    action.isPending ||
    isSubmitting ||
    (!isMedReq && !shareResultsTemplate) ||
    !canSendMessagesRole ||
    (isMedReq && filesGate.isBlocked) ||
    !!approvalWaitReason ||
    // the LINK layout clears the medication while any of its dropdowns is unselected, and the
    // page stays editable while this sidebar is open. Not needed once a multi product order's
    // opened request was decided on its card.
    (isMedReq && !medication && (!medRequest?.data || isOpenedTreatmentPending(medRequest.data)))

  const { shouldUseBatchApprovalLogic } = useBatchApproval({
    medRequest: medRequest?.data
  })
  const shouldResetJustification = !isMedReq || !shouldUseBatchApprovalLogic

  if (!approveAction || !medRequest) return null

  return (
    <div
      data-testid='approval-reason-section'
      className='h-fit flex min-w-[25vw] flex-col rounded-2xl border border-custom-neutral-200 shadow-[0px_0px_1px_0px_rgba(113,128,150,0.04),0px_4px_8px_0px_rgba(113,128,150,0.08)]'
    >
      <div className='p-6'>
        <div className='mb-2 flex justify-between items-center'>
          <p className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>Approval Reason</p>
          <X
            className='w-6 h-6 text-custom-neutral-600 cursor-pointer'
            data-testid='close-icon'
            onClick={() => setSidebarView('navigation-menu')}
          />
        </div>
        {isMedReq && (
          <p className='text-custom-neutral-500 text-sm leading-[170%]'>
            {`Provide the reasons for approving the ${isMedReq ? 'medication request' : 'investigation'}.`}
          </p>
        )}
      </div>
      <div className='p-6'>
        {!canSendMessagesRole && (
          <div className='py-4 px-6 bg-[#FFF9E6] rounded-3xl border border-[#F4D03F] mb-4'>
            <p className='text-sm leading-[170%] text-[#B7950B] font-normal'>
              You have read-only access. You cannot approve medication requests or investigations.
            </p>
          </div>
        )}
        {isMedReq && canSendMessagesRole && filesGate.isBlocked && (
          <div className='py-4 px-6 bg-[#FFF9E6] rounded-3xl border border-[#F4D03F] mb-4'>
            <p className='text-sm leading-[170%] text-[#B7950B] font-normal'>
              All patient files must be viewed before approving.
            </p>
          </div>
        )}
        {canSendMessagesRole && approvalWaitReason && (
          <div className='py-4 px-6 bg-[#FFF9E6] rounded-3xl border border-[#F4D03F] mb-4'>
            <p data-testid='approval-wait-reason' className='text-sm leading-[170%] text-[#B7950B] font-normal'>
              {approvalWaitReason}
            </p>
          </div>
        )}
        {isMedReq && canSendMessagesRole && (
          <>
            <p className='mb-4 text-[#1D1D1D] font-medium leading-[170%]'>Select a template</p>
            <SingleCombobox
              title='Templates'
              isDisabled={templateOptions?.approvalOptions.length === 0 || !canSendMessagesRole}
              defaultValue={template}
              onValueChange={value => {
                if (!canSendMessagesRole) return
                setJustification(value)
                setTemplate(value)
              }}
              options={templateOptions?.approvalOptions ?? []}
            />
            <div className='mt-4 mb-12'>
              <TextareaWithLabel
                value={justification}
                onChange={e => canSendMessagesRole && setJustification(e.target.value)}
                disabled={!canSendMessagesRole}
                autoFocus
              />
            </div>
          </>
        )}
        {!isMedReq && (
          <InvestigationTemplatePreview template={shareResultsTemplate} isLoading={isLoading} error={Boolean(error)} />
        )}
        {canSendMessagesRole && (
          <button
            type='button'
            data-testid='submit'
            onClick={async () => {
              if (!canSendMessagesRole || isSubmitting) return
              if (!fieldValidationNotifications('approve')) return

              setIsSubmitting(true)
              try {
                // An explicit false (only the med-request approve reports one) means the approve
                // kept the page for a retry — the sidebar then stays open with the justification
                // intact; the investigation approve is synchronous and closes as before.
                const result: unknown = await Promise.resolve(approveAction())
                if (result === false) return

                if (shouldResetJustification) {
                  setJustification('')
                }

                setSidebarView('navigation-menu')
              } finally {
                setIsSubmitting(false)
              }
            }}
            disabled={isDisabled}
            className={cn(
              'flex justify-center items-center gap-2 mb-4 h-14 rounded-2xl bg-[#153CB7] text-white text-sm font-semibold leading-[170%] w-full transition-all duration-200 ease-in-out',
              isDisabled && 'opacity-50 cursor-not-allowed',
              (action.isPending || isSubmitting) && 'cursor-wait opacity-70'
            )}
          >
            {action.isPending || isSubmitting ? (
              <>
                <LoadingSpinner iconClassName='w-6 h-6 text-white animate-spin' />
                <span>Loading..</span>
              </>
            ) : (
              'Submit'
            )}
          </button>
        )}
        <button
          type='button'
          data-testid='cancel'
          onClick={() => {
            setJustification('')
            setSidebarView('navigation-menu')
          }}
          className='flex h-14 w-full color-[#1D1D1D] items-center justify-center gap-4 rounded-2xl border border-custom-neutral-200 bg-white px-6 text-custom-neutral-600 text-sm font-semibold  hover:bg-custom-neutral-200 transition-colors duration-200
        leading-[170%]'
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export default ApprovalReasonSection
